import type { Env } from '../api/client';
import type { TenantSettings } from '../api/types';
import { normalizeStateCodes } from '../domain/geo';

/** One conditional-visibility rule, keyed on custom field `code` (never `id`). */
export interface ConditionalRule {
  when: { code: string; is: 'checked' | 'unchecked' };
  /** codes of dependent fields revealed when the condition holds */
  show: string[];
}

export interface RuntimeConfig {
  apiBase: string;
  env: Env;
  allowProduction: boolean;
  /** `booking/form?type=` — the config the client edits in Automaid admin (cleaning for this tenant) */
  formType: string;
  recaptchaSiteKey: string | null;
  gaMeasurementId: string | null;
  conditionals: ConditionalRule[];
  /** Sandbox workaround: tenant settings fetched server-side when the API answers a browser with an empty body. */
  settingsPreload: TenantSettings | null;
  /** `?vehicle=<category slug>` on the booking page preselects a service category */
  preselectVehicle: string | null;
  /** `?service=<name slug or id>` preselects a specific service (wins over `vehicle`) */
  preselectService: string | null;
  /** After a successful booking: on-page confirmation with the order summary, or the tenant's thank-you redirect. */
  afterBooking: 'summary' | 'redirect';
  /** State codes the business serves (e.g. ['OK']); empty means every state the tenant lists. */
  serviceStates: string[];
  /** Sandbox only: a `pk_test_…` key used instead of the tenant's own, for testing checkout. */
  stripeKeyOverride: string | null;
  /** dev-server only: serve fixtures through the mock transport */
  mock: boolean;
  debug: boolean;
}

export const DEFAULT_SANDBOX_BASE = 'https://chasedetailing-sandbox.launch27.com/v1';

type Injected = Partial<Omit<RuntimeConfig, 'mock' | 'debug'>>;

/**
 * Runtime config wins over build-time env. The WP plugin prints
 * `<script type="application/json" id="cbf-config">{…}</script>` from its settings.
 */
export function readRuntimeConfig(
  doc: Document = document,
  loc: Location = location,
): RuntimeConfig {
  let injected: Injected = {};
  const el = doc.getElementById('cbf-config');
  if (el?.textContent?.trim()) {
    try {
      injected = JSON.parse(el.textContent) as Injected;
    } catch {
      console.warn('[cbf] #cbf-config is not valid JSON; using defaults');
    }
  }
  const params = new URLSearchParams(loc.search);
  const env = (injected.env ?? import.meta.env.VITE_ENV ?? 'sandbox') as Env;
  return {
    apiBase: injected.apiBase ?? import.meta.env.VITE_API_BASE ?? DEFAULT_SANDBOX_BASE,
    env: env === 'production' ? 'production' : 'sandbox',
    allowProduction: injected.allowProduction === true,
    formType: injected.formType ?? import.meta.env.VITE_FORM_TYPE ?? 'cleaning',
    recaptchaSiteKey: injected.recaptchaSiteKey ?? null,
    gaMeasurementId: injected.gaMeasurementId ?? null,
    conditionals: Array.isArray(injected.conditionals) ? injected.conditionals : [],
    settingsPreload:
      injected.settingsPreload && typeof injected.settingsPreload === 'object'
        ? injected.settingsPreload
        : null,
    preselectVehicle: params.get('vehicle')?.trim() || null,
    preselectService: params.get('service')?.trim() || null,
    afterBooking: injected.afterBooking === 'redirect' ? 'redirect' : 'summary',
    serviceStates: normalizeStateCodes(
      Array.isArray(injected.serviceStates) ? injected.serviceStates : [],
    ),
    // Belt and braces with the plugin: the browser refuses the override outside the sandbox too.
    stripeKeyOverride: env === 'production' ? null : (injected.stripeKeyOverride ?? null),
    mock: import.meta.env.DEV && params.get('cbf_mock') === '1',
    debug: params.get('cbf_debug') === '1',
  };
}
